import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

/**
 * Header de tabela clicável para ordenação.
 *
 * Usado nas duas tabelas de Cobertura. Na tabela de segmentos a ordenação é
 * no cliente (13 linhas); na de contatos é no servidor, porque a lista é
 * paginada e ordenar apenas a página atual daria resultado errado.
 *
 * @param campo      chave da coluna
 * @param ordem      { campo, dir } estado atual
 * @param onOrdenar  callback que recebe a chave clicada
 * @param alinhar    'left' | 'right'
 */
const HeaderOrdenavel = ({ campo, ordem, onOrdenar, children, alinhar = 'left', darkMode, title }) => {
  const ativo = ordem?.campo === campo;
  const dir = ativo ? ordem.dir : null;

  const base = darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700';
  const ativoCor = darkMode ? 'text-orange-400' : 'text-orange-600';

  return (
    <th
      scope="col"
      aria-sort={ativo ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-4 py-2 text-xs font-medium uppercase select-none ${alinhar === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => onOrdenar(campo)}
        title={title || 'Ordenar por esta coluna'}
        className={`inline-flex items-center gap-1 uppercase font-medium transition-colors ${
          ativo ? ativoCor : base
        } ${alinhar === 'right' ? 'flex-row-reverse' : ''}`}
      >
        <span>{children}</span>
        {!ativo && <ChevronsUpDown size={12} className="opacity-40" />}
        {ativo && dir === 'asc' && <ChevronUp size={12} />}
        {ativo && dir === 'desc' && <ChevronDown size={12} />}
      </button>
    </th>
  );
};

/**
 * Alterna a ordenação: primeiro clique aplica a direção inicial da coluna,
 * cliques seguintes invertem. Colunas numéricas começam em 'desc', porque o
 * interesse costuma ser o maior valor (ex.: quem está em mais grupos).
 */
export const alternarOrdem = (ordemAtual, campo, dirInicial = 'asc') => {
  if (ordemAtual?.campo === campo) {
    return { campo, dir: ordemAtual.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { campo, dir: dirInicial };
};

/** Comparador para ordenação no cliente, com números, texto e booleanos. */
export const compararValores = (a, b, campo, dir) => {
  const va = a?.[campo];
  const vb = b?.[campo];
  const vazio = (v) => v === undefined || v === null || v === '';

  if (vazio(va) && vazio(vb)) return 0;
  if (vazio(va)) return 1;   // vazios sempre no fim
  if (vazio(vb)) return -1;

  let cmp;
  if (typeof va === 'number' && typeof vb === 'number') {
    cmp = va - vb;
  } else if (typeof va === 'boolean' && typeof vb === 'boolean') {
    cmp = (va ? 1 : 0) - (vb ? 1 : 0);
  } else {
    cmp = String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base', numeric: true });
  }
  return dir === 'desc' ? -cmp : cmp;
};

export default HeaderOrdenavel;
